import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Filters, SMsWithModels } from '@spo/logic';
import { VpLoader } from '@vtmn-play/react';
import { clsx } from 'clsx';
import { useRoles } from '../../../contexts/RolesContext';
import { Footer } from '../../footer';
import { ModelAccordion } from './modelAccordion';
import './modelsTable.scss';

interface ModelsTableProps {
  sMsWithModels: SMsWithModels[] | undefined;
  modelIsError: boolean;
  selectedFilters: Filters;
  dateFormatted?: string;
  isLoading: boolean;
  defaultModelIds: string[];
}

const LoadingModel = () => (
  <div
    data-testid="model-accordion-table-loading"
    className="model-accordion-table-loading"
  >
    <VpLoader size="medium" />
  </div>
);

export const ModelsTable = ({
  sMsWithModels,
  modelIsError,
  selectedFilters,
  dateFormatted,
  isLoading,
  defaultModelIds = [],
}: ModelsTableProps) => {
  const [selectedModelIds, setSelectedModelIds] =
    useState<string[]>(defaultModelIds);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasRoleProductLeader } = useRoles();

  const handleMassEdit = () => {
    navigate(
      `/personal-offer/mass-edit?modelIds=${selectedModelIds.join(',')}`,
    );
  };

  const cancelSelection = () => {
    setSelectedModelIds([]);
  };

  return (
    <div
      data-testid="model-accordion-container"
      id="model-accordion-container"
      className={clsx('models-table', {
        'with-footer': Boolean(selectedModelIds.length),
      })}
    >
      {isLoading && <LoadingModel />}
      {modelIsError ? (
        <p data-testid="model_error_500_msg">
          {t('models.error_500_model_message')}
        </p>
      ) : (
        sMsWithModels?.map((sm) => {
          const { smId, description, models } = sm;
          return (
            <ModelAccordion
              key={`model-accordion-${smId}`}
              smId={smId}
              description={description}
              offerDate={dateFormatted}
              models={models}
              selectedModelIds={selectedModelIds}
              setSelectedModelIds={setSelectedModelIds}
              selectedFiltersStatus={selectedFilters.status}
              withCheckboxes
            />
          );
        })
      )}
      {Boolean(selectedModelIds.length) && (
        <Footer
          ctaLabel={t('footer.edit-button')}
          validateAction={handleMassEdit}
          cancelAction={cancelSelection}
          isValidateActionDisabled={!hasRoleProductLeader}
          comment={`${selectedModelIds.length} ${t(
            'footer.comment.selected-model',
          )}`}
          tooltipComment={
            !hasRoleProductLeader ? t('no-access-page.edit-button') : ''
          }
        />
      )}
    </div>
  );
};
